// ContextualActionBar.js
//
// Bottom-center floating strip — the "verb" half of the noun-first UI.
// Shows actions that apply to whatever is currently selected. When nothing
// is selected, the bar is hidden entirely (nothing to act on).
//
// Why this shape:
//   - The old UI had ~20 mode buttons lined up on the right, none of which
//     knew anything about the current selection. Users had to toggle
//     "Edit Lines" or "Delete Lines" MODE, then click the line. The new
//     bar inverts that flow — select first, then act — which also lets us
//     collapse ~8 mode toggles (Edit Lines / Edit Line Points / Edit
//     Boundaries / Edit Boundary Points / Delete Lines / Assign Type...)
//     into a handful of context-aware buttons.
//   - Edit maps to whichever legacy mode matches the selection: 1 line ->
//     editLineMode, 1 line point -> editLinePointMode, 1 boundary ->
//     editBoundaryMode, 1 boundary point -> editBoundaryPointMode. The
//     canvas behavior is unchanged; only the discovery path changes.
//
// Selection shapes handled (in priority order — only one at a time):
//   - 1+ line point:          Coordinates · Edit · Delete
//   - 1 boundary point:       Coordinates · Edit · Delete
//   - 1 boundary:             Info · Edit · Delete
//   - exactly 1 line:         Length · Type · Assign Type · Repeat · Edit · Delete
//   - >1 lines:               Count · Assign Type (bulk) · Extend · Delete
//
// The five cases above fully partition the space of "something is
// selected" — point-level (line or boundary), boundary, one line, many
// lines. So `resolveKind` can only return null when the entire
// selection is empty, in which case we render nothing (the bar is a
// selection-driven surface; with no selection there's nothing to act
// on). If a new selectable kind is ever added, extend `resolveKind`
// and add a branch in `renderReadout` / `renderActions` — do NOT
// rely on a "generic fallback", since there's no sensible default
// action set for an unknown feature type.

import React from 'react';
import {
  Pencil,
  Trash2,
  Repeat,
  MoveHorizontal,
  Move,
  Tag,
} from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'turn_row',   label: 'Turn Row' },
  { value: 'inside_row', label: 'Inside Row' },
  { value: 'end_row',    label: 'End Row' },
  { value: 'reset_row',  label: 'Reset Row' },
];

// Small helper: count distinct items in the selection.
const selectionSummary = ({ selectedLine, selectedLinePoint, selectedBoundary, selectedBoundaryPoint, selectedMeasurement }) => {
  const lines = Array.isArray(selectedLine) ? selectedLine : (selectedLine ? [selectedLine] : []);
  const measurements = Array.isArray(selectedMeasurement)
    ? selectedMeasurement
    : (selectedMeasurement != null ? [selectedMeasurement] : []);
  return {
    lineCount: lines.length,
    lines,
    hasLinePoint: !!selectedLinePoint,
    hasBoundary: !!selectedBoundary,
    hasBoundaryPoint: !!selectedBoundaryPoint,
    hasMeasurement: measurements.length > 0,
    measurementCount: measurements.length,
    measurementIds: measurements,
  };
};

// Detect which selection shape we're in. Returns one of the kind strings
// or null if nothing is selected. Priority: point-level > feature-level,
// boundary > line (arbitrary but consistent). The five branches below
// are exhaustive for non-empty selections — see the file-header note if
// you're adding a new selectable kind.
const resolveKind = (sel) => {
  if (sel.hasLinePoint) return 'linePoint';
  if (sel.hasBoundaryPoint) return 'boundaryPoint';
  if (sel.hasBoundary) return 'boundary';
  if (sel.lineCount === 1) return 'oneLine';
  if (sel.lineCount > 1) return 'manyLines';
  if (sel.hasMeasurement) return 'measurement';
  return null;
};

// Compute geometric length in pixels for a line (sum of segment lengths).
// Callers can convert to real-world distance via gsd * pixels.
const lineLengthPx = (line) => {
  if (!line || !line.points || line.points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < line.points.length; i++) {
    const dx = line.points[i][0] - line.points[i - 1][0];
    const dy = line.points[i][1] - line.points[i - 1][1];
    total += Math.sqrt(dx * dx + dy * dy);
  }
  return total;
};

// Shared styles for the bar and its buttons. Kept inline so the component
// drops in without CSS plumbing; a design-token pass can centralize later.
const barStyle = {
  // Sits directly above the PrimaryToolbar (bottom: 16px, ~62px tall). We
  // park this at bottom: 94px so the two bars stack with a small gap and
  // never overlap regardless of content width.
  position: 'fixed',
  bottom: '94px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  backgroundColor: 'rgba(255,255,255,0.98)',
  border: '1px solid #cfd7d0',
  borderRadius: '10px',
  boxShadow: '0 6px 20px rgba(0,0,0,0.16)',
  zIndex: 1300,
  fontFamily: 'Arial, sans-serif',
  fontSize: '0.85rem',
};

const readoutStyle = {
  padding: '4px 10px',
  color: '#334',
  fontWeight: 600,
  borderRight: '1px solid #e4e8e4',
  whiteSpace: 'nowrap',
};

const chipStyle = {
  padding: '2px 8px',
  borderRadius: '4px',
  backgroundColor: '#eef2ee',
  color: '#334',
  fontWeight: 600,
  fontSize: '0.72rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
};

const btnStyle = (variant = 'default') => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.82rem',
    fontWeight: 600,
  };
  if (variant === 'danger') {
    return { ...base, backgroundColor: '#fdeceb', color: '#a02522' };
  }
  if (variant === 'primary') {
    return { ...base, backgroundColor: '#2e7d32', color: 'white' };
  }
  return { ...base, backgroundColor: '#eef2ee', color: '#223' };
};

// Format cm as inches or feet+inches. Mirrors the formatter in
// ZoomPanComponent so the readout matches what's painted on the canvas.
const formatDistance = (cm) => {
  const totalInches = cm / 2.54;
  if (totalInches < 12) return `${totalInches.toFixed(1)} in`;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches - feet * 12;
  return `${feet} ft ${inches.toFixed(1)} in`;
};

const ContextualActionBar = ({
  // selection
  selectedLine,
  selectedLinePoint,
  selectedBoundary,
  selectedBoundaryPoint,
  selectedMeasurement,
  completedMeasurements,
  // metadata
  gsd,
  // actions
  onEdit,            // () => void — parent resolves which edit mode
  onDelete,          // () => void — parent handles selection-aware delete
  onRepeat,          // () => void — only shown for exactly 1 line
  onExtend,          // () => void — only shown for 1+ lines
  onAssignType,      // (type: string) => void — used by inline select
  onMove,            // () => void — opens the precise-move dialog (⌘/Ctrl+Shift+M)
}) => {
  const sel = selectionSummary({
    selectedLine,
    selectedLinePoint,
    selectedBoundary,
    selectedBoundaryPoint,
    selectedMeasurement,
  });
  const kind = resolveKind(sel);

  // Hooks MUST be called unconditionally (rules-of-hooks), so declare them
  // before any early-return based on `kind`. Local state for the inline
  // "Assign Type" dropdown — parent only learns about type changes when
  // the user commits via Apply.
  const [pendingType, setPendingType] = React.useState('');

  // Reset the pending type whenever the selection shape or count changes
  // so the dropdown doesn't carry stale state across selections.
  React.useEffect(() => {
    setPendingType('');
  }, [kind, sel.lineCount]);

  if (!kind) return null; // nothing selected → no bar

  const applyType = () => {
    if (pendingType && onAssignType) onAssignType(pendingType);
    setPendingType('');
  };

  // Readout text for each kind. Lengths are in meters when gsd is known
  // (gsd is cm/px, so px * gsd / 100 = meters).
  const renderReadout = () => {
    if (kind === 'oneLine') {
      const line = sel.lines[0];
      const px = lineLengthPx(line);
      const meters = (typeof gsd === 'number' && gsd > 0) ? (px * gsd / 100) : null;
      const lenText = meters !== null ? `${meters.toFixed(2)} m` : `${px.toFixed(0)} px`;
      return (
        <span style={readoutStyle}>
          1 line · {lenText}
          {line.type ? <> · <span style={chipStyle}>{line.type.replace('_', ' ')}</span></> : null}
        </span>
      );
    }
    if (kind === 'manyLines') {
      return <span style={readoutStyle}>{sel.lineCount} lines selected</span>;
    }
    if (kind === 'linePoint') {
      const p = selectedLinePoint;
      const coords = Array.isArray(p) ? p : (p?.point || []);
      const x = coords[0]; const y = coords[1];
      const text = (typeof x === 'number' && typeof y === 'number') ? `(${Math.round(x)}, ${Math.round(y)})` : 'point';
      return <span style={readoutStyle}>Line point · {text}</span>;
    }
    if (kind === 'boundary') {
      const pts = selectedBoundary?.points?.length ?? 0;
      return <span style={readoutStyle}>1 boundary · {pts} pts</span>;
    }
    if (kind === 'boundaryPoint') {
      const p = selectedBoundaryPoint;
      const coords = Array.isArray(p) ? p : (p?.point || []);
      const x = coords[0]; const y = coords[1];
      const text = (typeof x === 'number' && typeof y === 'number') ? `(${Math.round(x)}, ${Math.round(y)})` : 'point';
      return <span style={readoutStyle}>Boundary point · {text}</span>;
    }
    if (kind === 'measurement') {
      const ids = sel.measurementIds;
      if (ids.length === 1) {
        const m = Array.isArray(completedMeasurements)
          ? completedMeasurements.find(x => x.id === ids[0])
          : null;
        const label = m && typeof m.distance === 'number' ? formatDistance(m.distance) : 'measurement';
        return <span style={readoutStyle}>Measurement · {label}</span>;
      }
      return <span style={readoutStyle}>{ids.length} measurements</span>;
    }
    return null;
  };

  // Action buttons per kind. Each kind returns an array of <button> nodes
  // so we can interleave them with the readout/separators cleanly.
  const renderActions = () => {
    const showAssignType = kind === 'oneLine' || kind === 'manyLines';
    const assignTypeBlock = showAssignType ? (
      <div key="assign-type" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Tag size={14} strokeWidth={1.75} style={{ color: '#556' }} />
        <select
          value={pendingType}
          onChange={(e) => setPendingType(e.target.value)}
          style={{
            fontSize: '0.8rem',
            padding: '4px 6px',
            borderRadius: 4,
            border: '1px solid #cfd7d0',
            backgroundColor: 'white',
          }}
        >
          <option value="">Assign type…</option>
          {TYPE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={applyType}
          disabled={!pendingType}
          style={{ ...btnStyle(pendingType ? 'primary' : 'default'), padding: '4px 10px' }}
        >
          Apply
        </button>
      </div>
    ) : null;

    const repeatBtn = kind === 'oneLine' ? (
      <button key="repeat" type="button" onClick={onRepeat} style={btnStyle()}>
        <Repeat size={14} strokeWidth={1.75} /> Repeat
      </button>
    ) : null;

    const extendBtn = (kind === 'oneLine' || kind === 'manyLines') ? (
      <button key="extend" type="button" onClick={onExtend} style={btnStyle()}>
        <MoveHorizontal size={14} strokeWidth={1.75} /> Extend
      </button>
    ) : null;

    // Precise numeric move. Offered for every geometry kind (measurements
    // are annotations and aren't movable), so the ⌘⇧M shortcut has a visible
    // affordance instead of being discoverable only via the ? overlay.
    const moveBtn = (onMove && kind !== 'measurement') ? (
      <button key="move" type="button" onClick={onMove} style={btnStyle()} title="Move selection by an exact amount (⌘/Ctrl+Shift+M)">
        <Move size={14} strokeWidth={1.75} /> Move…
      </button>
    ) : null;

    const editBtn = (kind !== 'manyLines' && kind !== 'measurement') ? (
      <button key="edit" type="button" onClick={onEdit} style={btnStyle()}>
        <Pencil size={14} strokeWidth={1.75} /> Edit
      </button>
    ) : null;

    const deleteBtn = (
      <button key="delete" type="button" onClick={onDelete} style={btnStyle('danger')}>
        <Trash2 size={14} strokeWidth={1.75} /> Delete
      </button>
    );

    return [assignTypeBlock, repeatBtn, extendBtn, moveBtn, editBtn, deleteBtn].filter(Boolean);
  };

  return (
    <div role="toolbar" aria-label="Actions for selection" style={barStyle}>
      {renderReadout()}
      {renderActions()}
    </div>
  );
};

export default ContextualActionBar;
